// shared-lib/cabinet-runs/runIds.js
//
// A DERIVED run is named for its first wall (`scanrun_<wallId>` /
// `autorun_<wallId>`). Once runs join at corners (step 4, #1494) one run spans
// several walls, and everything that named the run by another wall's id —
// step 3's `align-end` links, `runEndsForRoom`, a persisted override — must
// still find it. `aliases` are those other per-wall ids; this is the ONE place
// an id is resolved through them.

function perWallRunId(source, wallId) {
  return `${source === 'scan' ? 'scanrun' : 'autorun'}_${wallId}`;
}

// A shallow storage run is named for its STRETCH, not its wall: it stands on
// a wall beside that wall's own run (`kitchen-assembly/cabinetLayout/
// shallowRuns.js`), and the wall's id is that run's name. Answering to it
// made every edit of the shallow run treat the deep run's cabinets as its
// own — a delete took them, a re-solve swept them (2026-10-23).
const SHALLOW_RUN_ID_PREFIX = 'autorun_shallow_';

/** The ids a run answers to besides `run.id`: one per wall it spans. */
const DERIVED_SOURCES = new Set(['scan', 'auto']);

function runAliases(run) {
  if (!run) return [];
  const out = new Set(run.aliases || []);
  // Only a DERIVED run is named for its walls; a drawn run's id is its own,
  // and so is a shallow storage run's.
  if (!DERIVED_SOURCES.has(run.source)) return [...out];
  if (String(run.id || '').startsWith(SHALLOW_RUN_ID_PREFIX)) return [...out];
  for (const s of run.segments || []) {
    const wallId = s?.wallId || s?.wall?.id;
    if (!wallId) continue;
    const id = perWallRunId(run.source, wallId);
    if (id !== run.id) out.add(id);
  }
  return [...out];
}

// A run derived from a VANITY (`vanityToRun`) is named for the vanity's
// cabinet. Kept here, with no dependencies, rather than in `vanityToRun`:
// the vanity-assembly readers need to recognise a vanity's run, and
// `vanityToRun` itself reads through them.
const VANITY_RUN_PREFIX = 'vanityrun_';

/** The stored run id for a vanity group. Stable, so a re-derive is idempotent. */
function vanityRunId(cabinetId) {
  return `${VANITY_RUN_PREFIX}${cabinetId}`;
}

/** Is `id` a run derived from a vanity (`vanityRunId`)? */
function isVanityRunId(id) {
  return typeof id === 'string' && id.startsWith(VANITY_RUN_PREFIX);
}

function runAnswersTo(run, id) {
  return !!run && (run.id === id || runAliases(run).includes(id));
}

function findRunByIdOrAlias(runs, id) {
  if (!id) return null;
  return (
    (runs || []).find((r) => r?.id === id) ||
    (runs || []).find((r) => runAnswersTo(r, id)) ||
    null
  );
}

module.exports = {
  VANITY_RUN_PREFIX,
  vanityRunId,
  isVanityRunId,
  SHALLOW_RUN_ID_PREFIX,
  perWallRunId,
  runAliases,
  runAnswersTo,
  findRunByIdOrAlias,
};
